import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';

export default function Login() {
  const [employeeId, setEmployeeId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/dashboard';

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(employeeId, password);
    
    if (result.success) {
      // Redirect based on role
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const target = userData.role === 'admin' ? '/dashboard' : '/my-dashboard';
      navigate(from !== '/dashboard' ? from : target, { replace: true });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src="/seeza-logo.png" 
              alt="Seeza Trading Company" 
              className="h-24 w-auto object-contain"
            />
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="text-2xl font-semibold text-slate-800">Welcome back</CardTitle>
              <CardDescription className="text-slate-500">
                Sign in to access the attendance system
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm animate-fade-in" data-testid="login-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="employeeId" className="text-slate-700">Employee ID</Label>
                  <Input
                    id="employeeId"
                    type="text"
                    placeholder="Enter your employee ID"
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    required
                    className="h-11 border-slate-200 focus:border-[#FF9933] focus:ring-[#FF9933]"
                    data-testid="login-employee-id"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11 pr-10 border-slate-200 focus:border-[#FF9933] focus:ring-[#FF9933]"
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-[#FF9933] hover:bg-[#E68A2E] text-white font-medium shadow-md hover:shadow-lg transition-all duration-200"
                  data-testid="login-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </span>
                  )}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-slate-500">
                Need help? Contact your administrator
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Panel - Decorative */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1771732266994-01a0971e3399?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxvcmFuZ2UlMjBncmVlbiUyMGFic3RyYWN0JTIwbWluaW1hbCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc0OTQ0MzQ4fDA&ixlib=rb-4.1.0&q=85')"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF9933]/80 via-transparent to-[#138808]/80" />
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-center">
          <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6">
            <span className="text-white font-bold text-4xl">S</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            Attendance Management
          </h2>
          <p className="text-white/90 max-w-sm">
            6-digit Employee Code based attendance tracking for Seeza Trading Company
          </p>
          
          {/* Indian Tricolor Bar */}
          <div className="mt-8 flex gap-0 rounded-full overflow-hidden shadow-lg">
            <div className="w-16 h-2 bg-[#FF9933]" />
            <div className="w-16 h-2 bg-white" />
            <div className="w-16 h-2 bg-[#138808]" />
          </div>
        </div>
      </div>
    </div>
  );
}
